import { FC, useMemo } from 'react'
import IconPhone from 'assets/phone.svg?react'
import IconMail from 'assets/post-mail.svg?react'
import IconTime from 'assets/time-line.svg?react'
import cn from 'classnames'
import { getFormatPhoneNumber } from 'helpers/string'
import { TDayOfWeek } from 'modules/terminal/types'
import IconBox from 'remixicon/icons/Others/box-3-line.svg?react'
import IconInformation from 'remixicon/icons/System/information-line.svg?react'
import { TerminalScheduleItem } from 'src/schema'

import TerminalMap from '../TerminalMap'
import { formatTerminalLimits } from './formatTerminalLimits'
import { ModalTerminalProps, TDaysMapValue } from './types'

import s from './styles.module.less'

export const ModalTerminal: FC<ModalTerminalProps> = (props) => {
  const { terminal } = props
  const daysMap = new Map<TDayOfWeek, TDaysMapValue>([
    ['mon', { name: 'Пн', index: 1 }],
    ['tue', { name: 'Вт', index: 2 }],
    ['wed', { name: 'Ср', index: 3 }],
    ['thu', { name: 'Чт', index: 4 }],
    ['fri', { name: 'Пт', index: 5 }],
    ['sat', { name: 'Сб', index: 6 }],
    ['sun', { name: 'Вс', index: 0 }]
  ])

  const getDay = (engDay: TDayOfWeek) => daysMap.get(engDay)

  const getIsToday = (dayOfWeek: TDayOfWeek) => {
    const day = getDay(dayOfWeek)
    const now = new Date()

    return day ? day.index === now.getDay() : false
  }

  const coords = [Number(terminal?.latitude), Number(terminal?.longitude)]

  const scheduleByDayKey = useMemo(() => {
    if (!terminal || !terminal.schedule) return {} as Record<TerminalScheduleItem['dayOfWeek'], TerminalScheduleItem>

    return terminal.schedule.reduce<Record<TerminalScheduleItem['dayOfWeek'], TerminalScheduleItem>>(
      (acc, item) => {
        acc[item.dayOfWeek] = item

        return acc
      },
      {} as Record<TerminalScheduleItem['dayOfWeek'], TerminalScheduleItem>
    )
  }, [terminal])

  const formattedLimits = useMemo(
    () => formatTerminalLimits(terminal?.specification?.limits),
    [terminal?.specification?.limits]
  )

  const directionText = useMemo(() => {
    if (!terminal) return ''

    const { isDeparture, isDestination } = terminal

    if (isDeparture && isDestination) {
      return 'Приём грузов / Выдача грузов'
    }
    if (isDeparture) {
      return 'Только приём грузов'
    }
    if (isDestination) {
      return 'Только выдача грузов'
    }

    return ''
  }, [terminal])

  return (
    <div className={s.detailBody}>
      <div className={s.terminal}>
        <div className={s.address}>{terminal?.address}</div>
        <div className={s.coords}>
          {terminal?.latitude}° {terminal?.longitude}°
        </div>
        <div className={s.schedule}>
          <IconTime className="text-on-surface-variant" containerSize="nm" size="xs" />
          <div className="flex flex-col gap-1 text-body-md">
            {!!terminal &&
              !!terminal?.schedule?.length &&
              Array.from(daysMap.keys()).map((dayKey) => {
                const weekDay = daysMap.get(dayKey)
                const scheduleDay = scheduleByDayKey[dayKey]
                const isWeekend = !scheduleDay
                const isToday = getIsToday(dayKey)

                return (
                  <div
                    key={dayKey}
                    className={cn('flex items-center gap-4', {
                      'text-on-surface': !isToday && !isWeekend,
                      'text-positive': isToday,
                      'text-on-surface-variant': isWeekend
                    })}
                  >
                    {weekDay && <span className="w-6">{weekDay.name}</span>}
                    <span
                      className={cn('relative', {
                        'after:absolute after:-right-4 after:top-[calc((100%-8px)/2)] after:size-2 after:rounded-full after:bg-positive after:content-[""]':
                          isToday
                      })}
                    >
                      {isWeekend ? 'Выходной' : `${scheduleDay.from} – ${scheduleDay.to}`}
                    </span>
                  </div>
                )
              })}
          </div>
        </div>
        <div className="mt-4 flex items-center gap-2 text-body-md">
          <IconPhone className="text-on-surface-variant" containerSize="nm" size="xs" />
          <div className="flex flex-col">
            {terminal?.phones?.map((phone, index) => (
              <div key={index}>
                <a className={cn(s.supportPhone, 'text-body-md')} href={`tel:${getFormatPhoneNumber(phone)}`}>
                  {getFormatPhoneNumber(phone)}
                </a>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-4 flex items-center gap-2 text-body-md">
          <IconMail className="text-on-surface-variant" containerSize="nm" size="xs" />
          <div className="flex flex-col">
            {terminal?.emails?.map((email, index) => (
              <div key={index}>
                <a className={s.supportPhone} href={`mailto:${email}`}>
                  {email}
                </a>
              </div>
            ))}
          </div>
        </div>
        {(directionText || formattedLimits.length > 0) && (
          <div className="mt-4 flex items-start gap-2 text-body-md">
            <IconBox className="text-on-surface-variant" containerSize="nm" size="xs" />
            <div className="flex flex-col">
              {directionText && <div>{directionText}</div>}
              {formattedLimits.map((limit, index) => (
                <div key={index}>{limit}</div>
              ))}
            </div>
          </div>
        )}
        {terminal?.comment && (
          <div className="mt-4 flex items-center gap-2 text-body-md">
            <IconInformation className="text-on-surface-variant" containerSize="nm" size="xs" />
            <div>{terminal.comment}</div>
          </div>
        )}
      </div>
      <TerminalMap
        center={coords}
        className="mt-6 !h-[calc(100vh-420px)] md:!h-[calc(100vh-620px)]"
        height={400}
        name={terminal?.name}
        zoom={15}
      />
    </div>
  )
}
